import { MESSAGE_CONSTANTS } from '@/constants/message'
import { useConfirmStore } from '@/store/confirm.store'
import { ref, computed } from 'vue'

export const useConfirmMessage = () => {
  const confirmStore = useConfirmStore()

  // 삭제 확인
  const confirmDelete = (targetName = '해당 항목') => {
    return confirmStore.showConfirm({
      title: '삭제 확인',
      content: MESSAGE_CONSTANTS.COMMON.DELETE(`'${targetName}'`),
      confirmText: '삭제',
      cancelText: '취소'
    })
  }

  // 등록 확인
  const confirmInsert = (targetName = '항목') => {
    return confirmStore.showConfirm({
      title: '등록 확인',
      content: MESSAGE_CONSTANTS.COMMON.INSERT(targetName),
      confirmText: '등록',
      cancelText: '취소'
    })
  }

  // 수정 확인
  const confirmUpdate = (targetName = '항목') => {
    return confirmStore.showConfirm({
      title: '수정 확인',
      content: MESSAGE_CONSTANTS.COMMON.UPDATE(targetName),
      confirmText: '수정',
      cancelText: '취소'
    })
  }

  // 저장 확인
  const confirmSave = (targetName = '항목') => {
    return confirmStore.showConfirm({
      title: '저장 확인',
      content: MESSAGE_CONSTANTS.COMMON.SAVE(targetName),
      confirmText: '저장',
      cancelText: '취소'
    })
  }

  // 성공 알림 (확인 버튼만)
  const alertSuccess = (type = 'save') => {
    let message = MESSAGE_CONSTANTS.SUCCESS.SAVE
    let title = '처리 완료'

    switch (type) {
      case 'insert':
        message = MESSAGE_CONSTANTS.SUCCESS.INSERT
        title = '등록 완료'
        break
      case 'update':
        message = MESSAGE_CONSTANTS.SUCCESS.UPDATE
        title = '수정 완료'
        break
      case 'delete':
        message = MESSAGE_CONSTANTS.SUCCESS.DELETE
        title = '삭제 완료'
        break
    }

    return confirmStore.showConfirm({
      title,
      content: message,
      confirmText: '확인',
      cancelText: ''
    })
  }

  // 에러 알림 (확인 버튼만)
  const alertError = (message = '처리 중 오류가 발생했습니다.') => {
    return confirmStore.showConfirm({
      title: '오류',
      content: message,
      confirmText: '확인',
      cancelText: ''
    })
  }

  // 유효성 검사 에러
  const alertValidation = (fieldName, type = 'required', options = {}) => {
    let message = MESSAGE_CONSTANTS.VALID.REQUIRED(fieldName)

    switch (type) {
      case 'max':
        message = MESSAGE_CONSTANTS.VALID.MAX(fieldName, options.max || 100)
        break
      case 'min':
        message = MESSAGE_CONSTANTS.VALID.MIN(fieldName, options.min || 1)
        break
      case 'numtype':
        message = MESSAGE_CONSTANTS.VALID.NUMTYPE(fieldName)
        break
    }

    return confirmStore.showConfirm({
      title: '입력 오류',
      content: message,
      confirmText: '확인',
      cancelText: ''
    })
  }

  // 특수한 확인들
  const confirmCancel = () => {
    return confirmStore.showConfirm({
      title: '취소 확인',
      content: MESSAGE_CONSTANTS.COMMON.CANCEL,
      confirmText: '예',
      cancelText: '아니오'
    })
  }

  const confirmResetLoginCount = () => {
    return confirmStore.showConfirm({
      title: '로그인 횟수 초기화',
      content: MESSAGE_CONSTANTS.USER.INFO.RESET_LOGIN_CNT,
      confirmText: '초기화',
      cancelText: '취소'
    })
  }

  return {
    confirmDelete,
    confirmInsert,
    confirmUpdate,
    confirmSave,
    alertSuccess,
    alertError,
    alertValidation,
    confirmCancel,
    confirmResetLoginCount,
    // 원본 store도 접근 가능하게
    showConfirm: confirmStore.showConfirm
  }
}

// 기존 Alert 컴포넌트 방식을 위한 composable
export const useAlertMessage = () => {
  // 모달 상태 관리
  const showDeleteModal = ref(false)
  const showConfirmModal = ref(false)
  const showSuccessModal = ref(false)
  const showErrorModal = ref(false)
  const showValidationModal = ref(false)

  // 현재 작업 및 대상 정보
  const currentAction = ref('')
  const targetName = ref('')
  const currentError = ref('')
  const validationField = ref('')
  const validationType = ref('required')
  const validationOptions = ref({})

  // Promise resolvers
  let deleteResolver = null
  let confirmResolver = null

  // 동적 메시지 계산
  const deleteMessage = computed(() => {
    const target = targetName.value || '해당 항목'
    return MESSAGE_CONSTANTS.COMMON.DELETE(`'${target}'`)
  })

  const confirmMessage = computed(() => {
    if (isCustomConfirmMessage.value) {
      return customConfirmMessage.value
    }

    const target = targetName.value || '항목'
    switch (currentAction.value) {
      case 'insert':
        return MESSAGE_CONSTANTS.COMMON.INSERT(target)
      case 'update':
        return MESSAGE_CONSTANTS.COMMON.UPDATE(target)
      case 'save':
        return MESSAGE_CONSTANTS.COMMON.SAVE(target)
      case 'cancel':
        return MESSAGE_CONSTANTS.COMMON.CANCEL
      case 'resetLoginCount':
        return MESSAGE_CONSTANTS.USER.INFO.RESET_LOGIN_CNT
      default:
        return `${target}을/를 처리하시겠습니까?`
    }
  })

  const successMessage = computed(() => {
    if (isCustomSuccessMessage.value) {
      return customSuccessMessage.value
    }

    switch (currentAction.value) {
      case 'insert':
        return MESSAGE_CONSTANTS.SUCCESS.INSERT
      case 'update':
        return MESSAGE_CONSTANTS.SUCCESS.UPDATE
      case 'delete':
        return MESSAGE_CONSTANTS.SUCCESS.DELETE
      case 'save':
        return MESSAGE_CONSTANTS.SUCCESS.SAVE
      case 'select':
        return MESSAGE_CONSTANTS.SUCCESS.SELECT
      case 'restore':
        return MESSAGE_CONSTANTS.SUCCESS.RESTORE
      case 'forcedelete':
        return MESSAGE_CONSTANTS.SUCCESS.FORCEDELETE
      default:
        return MESSAGE_CONSTANTS.SUCCESS.SAVE
    }
  })

  const validationMessage = computed(() => {
    const field = validationField.value || '해당 항목'
    const type = validationType.value
    const options = validationOptions.value || {}

    switch (type) {
      case 'max':
        return MESSAGE_CONSTANTS.VALID.MAX(field, options.max || 100)
      case 'min':
        return MESSAGE_CONSTANTS.VALID.MIN(field, options.min || 1)
      case 'numtype':
        return MESSAGE_CONSTANTS.VALID.NUMTYPE(field)
      case 'required':
      default:
        return MESSAGE_CONSTANTS.VALID.REQUIRED(field)
    }
  })

  // Alert 제목들
  const confirmTitle = computed(() => {
    switch (currentAction.value) {
      case 'insert':
        return '등록 확인'
      case 'update':
        return '수정 확인'
      case 'save':
        return '저장 확인'
      case 'cancel':
        return '취소 확인'
      case 'resetLoginCount':
        return '로그인 횟수 초기화'
      default:
        return '확인'
    }
  })

  const successTitle = computed(() => {
    switch (currentAction.value) {
      case 'insert':
        return '등록 완료'
      case 'update':
        return '수정 완료'
      case 'delete':
        return '삭제 완료'
      case 'save':
        return '저장 완료'
      default:
        return '처리 완료'
    }
  })

  // 모달 표시 함수들
  const showDelete = (target = '해당 항목') => {
    return new Promise((resolve) => {
      targetName.value = target
      deleteResolver = resolve
      showDeleteModal.value = true
    })
  }

  const isCustomConfirmMessage = ref(false)
  const customConfirmMessage = ref('')

  const isCustomSuccessMessage = ref(false)
  const customSuccessMessage = ref('')

  const showConfirm = (action, target = '항목', options = {}) => {
    return new Promise((resolve) => {
      // 옵션에서 customMessage가 있는 경우 처리
      if (options.customMessage) {
        isCustomConfirmMessage.value = true
        customConfirmMessage.value = options.customMessage
      } else {
        isCustomConfirmMessage.value = false
        targetName.value = target
      }

      currentAction.value = action
      confirmResolver = resolve
      showConfirmModal.value = true
    })
  }

  const showSuccess = (action, options = {}) => {
    currentAction.value = action

    if (options.customMessage) {
      isCustomSuccessMessage.value = true
      customSuccessMessage.value = options.customMessage
    } else {
      isCustomSuccessMessage.value = false
      customSuccessMessage.value = ''
    }

    showSuccessModal.value = true
  }

  const showError = (message = '처리 중 오류가 발생했습니다.') => {
    currentError.value = message
    showErrorModal.value = true
  }

  const showValidation = (field, type = 'required', options = {}) => {
    validationField.value = field
    validationType.value = type
    validationOptions.value = options
    showValidationModal.value = true
  }

  // 모달 확인/취소 처리
  const confirmDelete = () => {
    showDeleteModal.value = false
    if (deleteResolver) {
      deleteResolver(true)
      deleteResolver = null
    }
  }

  const cancelDelete = () => {
    showDeleteModal.value = false
    if (deleteResolver) {
      deleteResolver(false)
      deleteResolver = null
    }
  }

  const confirmAction = () => {
    showConfirmModal.value = false
    if (confirmResolver) {
      confirmResolver(true)
      confirmResolver = null
    }
  }

  const cancelAction = () => {
    showConfirmModal.value = false
    if (confirmResolver) {
      confirmResolver(false)
      confirmResolver = null
    }
  }

  const closeSuccess = () => {
    showSuccessModal.value = false
    currentAction.value = ''
    isCustomSuccessMessage.value = false
    customSuccessMessage.value = ''
  }


  const closeError = () => {
    showErrorModal.value = false
    currentError.value = ''
  }

  const closeValidation = () => {
    showValidationModal.value = false
    validationField.value = ''
    validationType.value = 'required'
    validationOptions.value = {}
  }

  return {
    // 모달 상태
    showDeleteModal,
    showConfirmModal,
    showSuccessModal,
    showErrorModal,
    showValidationModal,

    // 동적 메시지
    deleteMessage,
    confirmMessage,
    successMessage,
    validationMessage,
    confirmTitle,
    successTitle,
    currentError,

    // 모달 표시 함수
    showDelete,
    showConfirm,
    showSuccess,
    showError,
    showValidation,

    // 모달 처리 함수
    confirmDelete,
    cancelDelete,
    confirmAction,
    cancelAction,
    closeSuccess,
    closeError,
    closeValidation
  }
}